import {OrderChangeRequestStatus} from '@hconnect/tuc-processes/orderManagement'
import {Alert, Icon} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {Box, CircularProgress, IconButton, Link, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from 'tss-react/mui'

import {useDismissFailedOrderChangeRequest} from '../../../../../common/react-query/hooks/mutations/useDismissFailedOrderChangeRequest'
import {useGetFailedOrderChangeRequests} from '../../../../../common/react-query/hooks/queries/useGetFailedOrderChangeRequests'

const useStyles = makeStyles()(() => ({
  alert: {
    '& > div:last-child': {
      flex: 1
    }
  },
  textContainer: {
    flex: 1
  },
  title: {
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '20px',
    marginBottom: 4
  },
  description: {
    fontSize: 14,
    lineHeight: '20px'
  },
  viewLink: {
    fontSize: 14,
    lineHeight: '20px',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  }
}))

interface FailedOrderChangeRequestBannerItemProps {
  request: OrderChangeRequestStatus
  onViewOrder: (orderId: string) => void
  onDismiss: (requestId: string) => void
}

const FailedOrderChangeRequestBannerItem: React.FC<FailedOrderChangeRequestBannerItemProps> = ({
  request,
  onViewOrder,
  onDismiss
}) => {
  const {t} = useTranslation()
  const {classes} = useStyles()

  return (
    <Alert
      variant="error"
      data-test-id={`failed-order-change-banner-${request.requestId}`}
      className={classes.alert}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: '100%'
        }}
      >
        <Box className={classes.textContainer}>
          <Typography className={classes.title}>
            {t(
              'orderIntake.rmc.orderManagement.failedOrderChangeBanner.title',
              'Not approved: change request for order #{{orderNumber}}',
              {orderNumber: request.orderNumber}
            )}
          </Typography>
          <Typography className={classes.description}>
            {t(
              'orderIntake.rmc.orderManagement.failedOrderChangeBanner.description',
              'If you have any doubts regarding this change request, please contact dispatch.'
            )}
          </Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
          <Link
            component="button"
            className={classes.viewLink}
            onClick={() => onViewOrder(request.orderId)}
            data-test-id={`view-order-link-${request.requestId}`}
          >
            {t(
              'orderIntake.rmc.orderManagement.failedOrderChangeBanner.viewOrder',
              'View this order'
            )}
          </Link>
          <IconButton
            onClick={() => onDismiss(request.requestId)}
            size="small"
            aria-label="Dismiss banner"
            data-test-id={`dismiss-failed-order-change-banner-${request.requestId}`}
            sx={{
              padding: '4px'
            }}
          >
            <Icon path={mdiClose} size="regular" />
          </IconButton>
        </Box>
      </Box>
    </Alert>
  )
}

interface FailedOrderChangeRequestBannerProps {
  /** Start date for filtering failed requests (ISO date string) */
  startDate: string | undefined
  /** Callback when "View this order" is clicked */
  onViewOrder: (orderId: string) => void
  /** List of order IDs currently visible in the orders list */
  visibleOrderIds: string[]
}

export const FailedOrderChangeRequestBanner: React.FC<FailedOrderChangeRequestBannerProps> = ({
  startDate,
  onViewOrder,
  visibleOrderIds
}) => {
  const {data: failedRequests, isLoading} = useGetFailedOrderChangeRequests(startDate)
  const {mutate: dismissRequest} = useDismissFailedOrderChangeRequest()

  // Filter to only show banners for orders currently visible in the list
  const visibleFailedRequests = React.useMemo(() => {
    if (!failedRequests) return []
    const visibleOrderIdSet = new Set(visibleOrderIds)
    return failedRequests.filter((request) => visibleOrderIdSet.has(request.orderId))
  }, [failedRequests, visibleOrderIds])

  const handleDismiss = (requestId: string) => {
    dismissRequest({requestId})
  }

  if (isLoading) {
    return (
      <Box py={1} display="flex" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (visibleFailedRequests.length === 0) {
    return null
  }

  return (
    <Box py={1} display="flex" flexDirection="column" gap={1}>
      {visibleFailedRequests.map((request) => (
        <FailedOrderChangeRequestBannerItem
          key={request.requestId}
          request={request}
          onViewOrder={onViewOrder}
          onDismiss={handleDismiss}
        />
      ))}
    </Box>
  )
}
